import type { Allowance, Discount, Product } from "~/api/products/types";
import { type BasketStore } from "~/providers/store/basket";
import { type ProductBasicWithTaxRate } from "~/providers/store/basket/types";
import { type KioskSession } from "~/stores/kiosk-session/types";

import { calculateUnitPriceByUnitSystem } from "../calculateUnitPriceByUnitSystem";

type BasketItems = KioskSession["basket"] | BasketStore["products"];
type Products = Product[] | ProductBasicWithTaxRate[];

type CalculateAllowanceArgs = {
  allowances: Allowance[];
  discounts: Discount[];
  products: Products;
  basket: BasketItems;
};

export function calculateAllowance({
  allowances,
  discounts,
  basket,
  products
}: CalculateAllowanceArgs): Allowance[] {
  if (!allowances.length) return [];

  return allowances.map((allowance) => {
    const discount = discounts.find((d) => d.id === allowance.discountId);

    if (!discount) return allowance;

    const productIds = products
      .filter((product) => product.discount?.id === discount.id)
      .map((product) => product.id);

    if (!productIds.length) return allowance;

    const quantityInBasket = basket.reduce((acc, item) => {
      if (productIds.includes(item.productId)) {
        if ("quantity" in item) {
          return acc + item.quantity;
        }

        if ("count" in item) {
          return acc + item.count;
        }
      }

      return acc;
    }, 0);

    if (!quantityInBasket) return allowance;

    let remaining = 0;

    if (allowance.type === "unitPrice") {
      const appliedUnitPriceDiscount = basket.reduce((acc, item) => {
        if (productIds.includes(item.productId)) {
          const product = products.find((p) => p.id === item.productId);
          if (!product) return acc;

          if (discount.amountType === "currency") return acc;

          const unitDiscountedPrice = calculateUnitPriceDiscounted({
            product,
            item,
            discount
          });

          return acc + unitDiscountedPrice;
        }

        return acc;
      }, 0);

      remaining = allowance.remaining - appliedUnitPriceDiscount;
    }

    if (allowance.type === "numberOfProducts") {
      remaining = allowance.remaining - quantityInBasket;
    }

    return {
      ...allowance,
      isAllowanceOver: remaining <= 0,
      remaining: Math.max(0, remaining)
    };
  });
}

type CalculateUnitPriceDiscountedArgs = {
  product: Products[number];
  item: BasketItems[number];
  discount: Discount;
};

function calculateUnitPriceDiscounted({
  product,
  item,
  discount
}: CalculateUnitPriceDiscountedArgs): number {
  const baseUnitPrice =
    "unitPrice" in product ? product.unitPrice : product.unitPriceInclVat;

  const effectiveUnitPrice =
    item.type === "variant-weight"
      ? calculateUnitPriceByUnitSystem({
          unitPrice: baseUnitPrice,
          weight: item.options[0].choices[0].weight,
          unitSystem: item.unitSystem,
          weightUnit: item.options[0].choices[0].weightUnit
        })
      : baseUnitPrice;

  const unitDiscountedPrice = (effectiveUnitPrice * discount.amount) / 100;

  if ("quantity" in item) {
    return unitDiscountedPrice * item.quantity;
  }

  if ("count" in item) {
    return unitDiscountedPrice * item.count;
  }

  throw new Error("Invalid item type");
}
